import '../../shared/components/styles/CustomControls.scss';
import './MainLayout.scss';
import '../../shared/styles/Modules.scss';
import { Fragment, useEffect, useState } from 'react';
import Avatar from '../../shared/components/Avatar/Avatar';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import Badge from '../../shared/components/Badge/Badge';
import Collapse from '../../shared/components/Collapse/Collapse';
import IconButton from '../../shared/components/IconButton/IconButton';
import List, { ListItemButton, ListItemIcon, ListItemText } from '../../shared/components/List/List';
import Menu, { MenuItem } from '../../shared/components/Menu/Menu';
import Tooltip from '../../shared/components/Tooltip/Tooltip';
import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import ConstructionOutlinedIcon from '@mui/icons-material/ConstructionOutlined';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import DashboardOutlinedIcon from '@mui/icons-material/DashboardOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import HelpCenterOutlinedIcon from '@mui/icons-material/HelpCenterOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import LightModeIcon from '@mui/icons-material/LightMode';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import RequestQuoteOutlinedIcon from '@mui/icons-material/RequestQuoteOutlined';
import TaskAltOutlinedIcon from '@mui/icons-material/TaskAltOutlined';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import PeopleAtIcon from '@mui/icons-material/PeopleAltOutlined';
import Box from '../../shared/components/Box/Box';
import AppBar from '../../shared/components/AppBar/AppBar';
import Drawer from '../../shared/components/Drawer/Drawer';
import PageHeader from '../../shared/components/PageHeader/PageHeader';
import Typography from '../../shared/components/Typography/Typography';
import GlobalSearchInput from '../../features/global-search/components/GlobalSearchInput';
import '../../features/global-search/styles/global-search.scss';
import { useThemeMode } from '../../theme/ThemeModeProvider';

const getUserName = (user) =>
  user?.name || user?.fullName || user?.username || user?.email || 'Mon compte';

const getInitials = (user) =>
  getUserName(user).split(/[\s.@_-]+/).filter(Boolean).slice(0, 2)
    .map((part) => part[0]).join('').toUpperCase();

function Sidebar({
  collapsed = false,
  currentUser,
  mode,
  onClose,
  onLogout,
  onToggleCollapsed,
  onToggleTheme,
  open = true,
  variant = 'permanent',
}) {
  const location = useLocation();
  const [affairesExpanded, setAffairesExpanded] = useState(false);
  const [userAnchor, setUserAnchor] = useState(null);

  const menuSections = [
    {
      label: 'Pilotage',
      items: [{ label: 'Tableau de bord', path: '/dashboard', icon: DashboardOutlinedIcon }],
    },
    {
      label: 'Activité',
      items: [
        { label: 'Stats CRM', path: '/crm', icon: AnalyticsIcon },
        {
          label: 'Affaires',
          path: '/affaires',
          icon: BusinessCenterOutlinedIcon,
          children: [
            { label: 'Projets', path: '/projets', icon: FolderOutlinedIcon },
            { label: 'Interventions', path: '/interventions', icon: ConstructionOutlinedIcon },
            { label: 'Devis', path: '/devis', icon: RequestQuoteOutlinedIcon },
          ],
        },
        { label: 'Prospection', path: '/prospection', icon: PeopleAtIcon },
        { label: 'Partenaires', path: '/partenaires', icon: HandshakeOutlinedIcon },
        { label: 'Planning', path: '/planning', icon: CalendarMonthOutlinedIcon },
        { label: 'Temps', path: '/temps', icon: AccessTimeOutlinedIcon },
      ],
    },
    {
      label: 'Ressources',
      items: [
        { label: 'GED', path: '/ged', icon: DescriptionOutlinedIcon },
        { label: 'Catalogue matériel', path: '/materiel/catalogue', icon: Inventory2OutlinedIcon },
        { label: 'Wiki', path: '/wiki', icon: MenuBookOutlinedIcon },
        { label: 'Mon espace', path: '/rh', icon: PersonOutlinedIcon },
      ],
    },
    {
      label: 'Gestion interne',
      items: [
        { label: 'Administratif', path: '/administratif', activePaths: ['/stock'], icon: AdminPanelSettingsOutlinedIcon },
        { label: 'Tâches', path: '/tasks', icon: TaskAltOutlinedIcon },
      ],
    },
  ];

  useEffect(() => {
    const matches = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);
    setAffairesExpanded(['/affaires', '/projets', '/interventions', '/devis'].some(matches));
  }, [location.pathname]);

  const isRouteActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const renderItem = (item) => {
    const ItemIcon = item.icon;
    if (item.children) {
      const isExpanded = affairesExpanded;
      const setExpanded = setAffairesExpanded;
      const isActive = item.children.some((child) => isRouteActive(child.path)) || isRouteActive(item.path);
      return (
        <Fragment key={item.path}>
          <ListItemButton
            component={RouterLink}
            to={item.path}
            selected={isActive}
            className="erp-sidebar-nav__item"
            onClick={onClose}
          >
            <ListItemIcon><ItemIcon fontSize="small" /></ListItemIcon>
            <ListItemText primary={item.label} />
            {!collapsed && (
              <IconButton
                className="erp-sidebar-nav__expand"
                size="small"
                aria-label={`${isExpanded ? 'Replier' : 'Déplier'} la catégorie ${item.label}`}
                aria-expanded={isExpanded}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setExpanded((current) => !current);
                }}
              >
                {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            )}
          </ListItemButton>
          <Collapse in={!collapsed && isExpanded} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {item.children.map((child) => {
                const ChildIcon = child.icon;
                return (
                  <ListItemButton
                    key={child.path}
                    component={RouterLink}
                    to={child.path}
                    selected={isRouteActive(child.path)}
                    className="erp-sidebar-nav__item erp-sidebar-nav__item--nested"
                    onClick={onClose}
                  >
                    <ListItemIcon><ChildIcon fontSize="small" /></ListItemIcon>
                    <ListItemText primary={child.label} />
                  </ListItemButton>
                );
              })}
            </List>
          </Collapse>
        </Fragment>
      );
    }

    const button = (
      <ListItemButton
        component={RouterLink}
        to={item.path}
        selected={isRouteActive(item.path) || Boolean(item.activePaths?.some(isRouteActive))}
        className="erp-sidebar-nav__item"
        onClick={onClose}
      >
        <ListItemIcon><ItemIcon fontSize="small" /></ListItemIcon>
        <ListItemText primary={item.label} />
      </ListItemButton>
    );
    return (
      <Fragment key={item.path}>
        {collapsed ? <Tooltip title={item.label} placement="right">{button}</Tooltip> : button}
      </Fragment>
    );
  };

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={onClose}
      rootClassName={`erp-sidebar-drawer${collapsed ? ' erp-sidebar-drawer--collapsed' : ''}`}
      className={`erp-sidebar-drawer__panel${collapsed ? ' erp-sidebar-drawer__panel--collapsed' : ''}`}
    >
      <div className="erp-sidebar-brand">
        <span className="erp-sidebar-brand__mark" aria-hidden="true">ICC</span>
        {!collapsed && (
          <span className="erp-sidebar-brand__copy">
            <strong>ICC ERP</strong>
            <small>Bureau d'études</small>
          </span>
        )}
      </div>
      <List className="erp-sidebar-nav" aria-label="Navigation principale">
        {menuSections.map((section) => (
          <li className="erp-sidebar-nav__section" key={section.label}>
            {!collapsed && <span className="erp-sidebar-nav__label">{section.label}</span>}
            <List disablePadding>{section.items.map(renderItem)}</List>
          </li>
        ))}
      </List>
      <div className="erp-sidebar-footer">
        <Tooltip title={collapsed ? getUserName(currentUser) : ''} placement="right">
          <button
            type="button"
            className="erp-drawer-user"
            onClick={(event) => setUserAnchor(event.currentTarget)}
            aria-label={`Ouvrir le menu de ${getUserName(currentUser)}`}
          >
            <Avatar>{getInitials(currentUser)}</Avatar>
            {!collapsed && (
              <>
                <span className="erp-drawer-user__copy">
                  <strong>{getUserName(currentUser)}</strong>
                  <small>{currentUser?.jobTitle || currentUser?.role || 'Collaborateur'}</small>
                </span>
                <ExpandMoreIcon fontSize="small" />
              </>
            )}
          </button>
        </Tooltip>
        <Menu
          anchorEl={userAnchor}
          open={Boolean(userAnchor)}
          onClose={() => setUserAnchor(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        >
          <MenuItem component={RouterLink} to="/rh" onClick={() => setUserAnchor(null)}>
            <PersonOutlinedIcon fontSize="small" /> Mon profil
          </MenuItem>
          <MenuItem onClick={() => { onToggleTheme(); setUserAnchor(null); }}>
            {mode === 'dark' ? <LightModeIcon fontSize="small" /> : <DarkModeIcon fontSize="small" />}
            {mode === 'dark' ? 'Thème clair' : 'Thème sombre'}
          </MenuItem>
          <MenuItem onClick={onLogout}><LogoutIcon fontSize="small" /> Déconnexion</MenuItem>
        </Menu>
      </div>
    </Drawer>
  );
}

export default function MainLayoutFrame({ currentUser, userTasks = [], onLogout, children }) {
  const { mode, toggleTheme } = useThemeMode();
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => localStorage.getItem('sas-erp-sidebar-collapsed') === 'true');
  const handleLogout = onLogout;
  const toggleSidebar = () => setSidebarCollapsed(current => {
    localStorage.setItem('sas-erp-sidebar-collapsed', String(!current)); return !current;
  });
  return (
    <Box className={`erp-app-shell${sidebarCollapsed ? ' erp-app-shell--sidebar-collapsed' : ''}`}>
      <AppBar className="erp-app-header" position="fixed">
        <div className="erp-app-header__toolbar">
          <Box className="erp-app-header__group">
            <IconButton
              color="inherit"
              edge="start"
              aria-label="Afficher ou masquer la navigation principale"
              onClick={() => {
                if (window.matchMedia('(max-width: 899px)').matches) setMobileSidebarOpen(true);
                else toggleSidebar();
              }}
            >
              <MenuIcon />
            </IconButton>
            <Typography className="erp-app-title" variant="h6">ICC ERP</Typography>
          </Box>
          <GlobalSearchInput />
          <Box className="erp-app-header__actions">
            <IconButton
              color="inherit"
              onClick={toggleTheme}
              title={mode === 'dark' ? 'Activer le mode clair' : 'Activer le mode sombre'}
              aria-label={mode === 'dark' ? 'Activer le mode clair' : 'Activer le mode sombre'}
            >
              {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
            </IconButton>
            <IconButton color="inherit" title="Centre d'aide" aria-label="Centre d'aide">
              <HelpCenterOutlinedIcon />
            </IconButton>
            <IconButton
              color="inherit"
              onClick={(event) => setNotificationsAnchor(event.currentTarget)}
              aria-label={`${userTasks.length} notification${userTasks.length > 1 ? 's' : ''}`}
            >
              <Badge badgeContent={userTasks.length || null} color="error"><NotificationsIcon /></Badge>
            </IconButton>
            <Menu
              anchorEl={notificationsAnchor}
              open={Boolean(notificationsAnchor)}
              onClose={() => setNotificationsAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
              {userTasks.length === 0 ? (
                <MenuItem disabled>Aucune tâche assignée</MenuItem>
              ) : userTasks.map((task) => (
                <MenuItem key={task.id} onClick={() => setNotificationsAnchor(null)}>
                  <Typography variant="body2">{task.title}</Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </div>
      </AppBar>

      <Sidebar
        collapsed={sidebarCollapsed}
        currentUser={currentUser}
        mode={mode}
        onLogout={handleLogout}
        onToggleCollapsed={toggleSidebar}
        onToggleTheme={toggleTheme}
      />
      <Sidebar
        currentUser={currentUser}
        mode={mode}
        variant="temporary"
        open={mobileSidebarOpen}
        onClose={() => setMobileSidebarOpen(false)}
        onLogout={handleLogout}
        onToggleTheme={toggleTheme}
      />

      <Box component="main" className="main-content" sx={{ flexGrow: 1, minWidth: 0 }}>
        <div className="erp-app-header-spacer" aria-hidden="true" />
        <div className="erp-page-content">
          <PageHeader />
          {children}
        </div>
      </Box>
    </Box>
  );
}
